/**
 * Manage my data — the client half of granular erasure (Phase 118-08).
 *
 * Server: `supabase/functions/data-item-delete`, one POST with `action`:
 *   summary                         200 { summary: { counts, team_companies, analytics_opt_out } }
 *   list    { category }            200 { items }
 *   delete  { category, id|ids|all } 200 { deleted, in_progress }
 *                                   207 { …, failed: ['storage'] }   rows gone, a file was not
 *                                   409 erasure_hold                 a company is being transferred
 *   analytics_preference { opt_out } 200 { analytics_opt_out }
 * Every action is behind the AAL2 boundary: an enrolled AAL1 session gets 403
 * `aal2_required`, and the screen runs the in-place step-up (StepUpModal).
 *
 * The server decides what is yours. The client never filters what it is shown
 * into "deletable" — it shows what the server lists, so the list cannot promise
 * a deletion the server would refuse. Branch on `code` and status, never text.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { isBackendEnabled, isMockMode } from '@/lib/config/app-mode';
import { reportError } from '@/lib/errors';
import { useAuth } from '@/lib/supabase';
import { setAnalyticsConsent } from '@/lib/analytics';
import { queryKeys } from '@/lib/supabase/api';
import { deckAnalysesQueryKey } from '@/lib/investor/use-deck-analyses';
import { dataRoomViewKeys } from '@/lib/dataroom/use-dataroom-views';
import { fetchFounderStartupId } from '@/lib/interview/use-founder-startup';
import { APP_LOCALE } from '@/lib/format';

// -----------------------------------------------------------------------------
// Categories
// -----------------------------------------------------------------------------

export type DataCategory =
  | 'data_room_document'
  | 'deck_analysis'
  | 'dd_conversation'
  | 'dd_note'
  | 'feedback'
  | 'analytics_events'
  | 'avatar';

export type Audience = 'founder' | 'investor' | 'everyone';

export interface CategoryMeta {
  key: DataCategory;
  label: string;
  /** What deleting it does, in one line. */
  description: string;
  audience: Audience;
  /** items: listed and deleted one by one (or all). whole: deleted as a whole only. */
  mode: 'items' | 'whole';
  /** Singular noun for confirmations ("this document"). */
  noun: string;
}

export const CATEGORIES: readonly CategoryMeta[] = [
  {
    key: 'data_room_document',
    label: 'Data room documents',
    description: 'Documents of companies only you run. The file, its text index and investor access to it are removed.',
    audience: 'founder',
    mode: 'items',
    noun: 'document',
  },
  {
    key: 'deck_analysis',
    label: 'Deck analyses',
    description: 'Pitch decks you uploaded for analysis. The deck file is deleted; the company stays in your pipeline.',
    audience: 'investor',
    mode: 'items',
    noun: 'deck analysis',
  },
  {
    key: 'dd_conversation',
    label: 'Due-diligence chats',
    description: 'Your conversations with the DD assistant, with every message in them.',
    audience: 'investor',
    mode: 'items',
    noun: 'conversation',
  },
  {
    key: 'dd_note',
    label: 'Due-diligence notes',
    description: 'Notes you wrote in shared due diligence, with the reactions to them.',
    audience: 'investor',
    mode: 'items',
    noun: 'note',
  },
  {
    key: 'feedback',
    label: 'Feedback you sent',
    description: 'Bug reports and suggestions, with any screenshot attached.',
    audience: 'everyone',
    mode: 'items',
    noun: 'feedback report',
  },
  {
    key: 'analytics_events',
    label: 'Usage analytics',
    description: 'The record of how you used the app. You can also stop it being collected.',
    audience: 'everyone',
    mode: 'whole',
    noun: 'usage history',
  },
  {
    key: 'avatar',
    label: 'Profile photo',
    description: 'Your uploaded photo, including earlier ones.',
    audience: 'everyone',
    mode: 'whole',
    noun: 'profile photo',
  },
];

export function categoryMeta(key: DataCategory): CategoryMeta {
  const meta = CATEGORIES.find((c) => c.key === key);
  if (!meta) throw new Error(`unknown category ${key}`);
  return meta;
}

/** The person's persona, as useUserRole() reports it. */
export type ViewerRole = 'startup' | 'investor' | 'admin' | 'pending' | null | undefined;

/**
 * Which categories this person sees. Pure; exported for tests.
 *
 * A founder sees founder + everyone categories, an investor investor +
 * everyone. Anyone else (an admin, a pending persona) sees the everyone
 * categories plus any other category that actually holds their data — data
 * must never be hidden from its owner because of how their role resolves.
 * Entitlement is deliberately NOT a factor: a lapsed subscriber still owns the
 * deck analyses they made while subscribed.
 */
export function visibleCategories(role: ViewerRole, counts?: Partial<Record<DataCategory, number>>): CategoryMeta[] {
  const audience: Audience | null = role === 'startup' ? 'founder' : role === 'investor' ? 'investor' : null;
  return CATEGORIES.filter(
    (c) => c.audience === 'everyone' || c.audience === audience || (counts?.[c.key] ?? 0) > 0,
  );
}

// -----------------------------------------------------------------------------
// Shapes
// -----------------------------------------------------------------------------

export interface DataSummary {
  counts: Record<DataCategory, number>;
  /** Companies you run with others: their documents are team data, managed in the data room. */
  teamCompanies: { startupId: string; name: string }[];
  analyticsOptOut: boolean;
}

export interface DataItem {
  id: string;
  title: string;
  subtitle: string | null;
  detail: string | null;
  createdAt: string | null;
}

export interface DeleteOutcome {
  deleted: number;
  /** Items skipped because they are still being processed (a live deck analysis). */
  inProgress: number;
}

export type DeleteTarget = { ids: string[] } | { all: true };

// -----------------------------------------------------------------------------
// Errors
// -----------------------------------------------------------------------------

export type ManageDataErrorKind =
  | 'step_up_required'
  | 'demo_account'
  | 'erasure_hold'
  | 'storage_incomplete'
  | 'unavailable_offline'
  | 'invalid_response'
  | 'failed';

export class ManageDataError extends Error {
  readonly kind: ManageDataErrorKind;
  readonly userMessage: string;
  readonly status?: number;
  /** For storage_incomplete: what WAS deleted (the rows are gone). */
  readonly outcome?: DeleteOutcome;

  constructor(kind: ManageDataErrorKind, userMessage: string, opts: { detail?: string; status?: number; outcome?: DeleteOutcome } = {}) {
    super(`manage_data_${kind}${opts.detail ? `: ${opts.detail}` : ''}`);
    this.name = 'ManageDataError';
    this.kind = kind;
    this.userMessage = userMessage;
    this.status = opts.status;
    this.outcome = opts.outcome;
  }
}

const GENERIC = 'Your request could not be completed. Please try again.';

function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : {};
}

/** Map a non-2xx status + body to the typed error. Pure; exported for tests. */
export function mapRefusal(status: number, body: Record<string, unknown>): ManageDataError {
  const code = typeof body.code === 'string' ? body.code : null;
  switch (code) {
    case 'aal2_required':
      return new ManageDataError(
        'step_up_required',
        'For your security, confirm it is you with the code from your authenticator app.',
        { detail: code, status },
      );
    case 'demo_account':
      return new ManageDataError(
        'demo_account',
        'This is a shared demo account, used by everyone who tries Inverio, so its data cannot be deleted.',
        { detail: code, status },
      );
    case 'erasure_hold':
      return new ManageDataError(
        'erasure_hold',
        'A company you belong to is being transferred after an account deletion. Try again in a day or two.',
        { detail: code, status },
      );
    default:
      return new ManageDataError('failed', GENERIC, { detail: code ?? `http_${status}`, status });
  }
}

// -----------------------------------------------------------------------------
// Transport
// -----------------------------------------------------------------------------

interface Reply {
  status: number;
  body: Record<string, unknown>;
}

export function manageDataAvailable(): boolean {
  return isBackendEnabled() && !isMockMode();
}

async function call(action: string, extra: Record<string, unknown> = {}): Promise<Reply> {
  if (!manageDataAvailable()) {
    throw new ManageDataError('unavailable_offline', 'Managing your data needs a live account. It is not available in offline sample mode.');
  }
  const { data, error, response } = await supabase.functions.invoke('data-item-delete', {
    body: { action, ...extra },
  });
  if (!error) return { status: response?.status ?? 200, body: asRecord(data) };

  const err = error as { name?: unknown; message?: unknown; context?: { status?: unknown; json?: () => Promise<unknown> } };
  if (err.name !== 'FunctionsHttpError' || typeof err.context?.status !== 'number') {
    throw new ManageDataError('failed', GENERIC, { detail: typeof err.message === 'string' ? err.message : String(error) });
  }
  let body: Record<string, unknown> = {};
  try {
    body = asRecord(await err.context.json?.());
  } catch (parseErr) {
    reportError(parseErr, { context: `manageData.${action}.errorBody`, level: 'warning' });
  }
  return { status: err.context.status, body };
}

function invalid(detail: string): ManageDataError {
  return new ManageDataError('invalid_response', GENERIC, { detail });
}

// -----------------------------------------------------------------------------
// Parsers (pure; exported for tests)
// -----------------------------------------------------------------------------

const CATEGORY_KEYS = CATEGORIES.map((c) => c.key);

export function parseSummary(body: Record<string, unknown>): DataSummary {
  const s = asRecord(body.summary);
  const rawCounts = asRecord(s.counts);
  if (!('counts' in s)) throw invalid('summary: missing counts');
  const counts = Object.fromEntries(
    CATEGORY_KEYS.map((k) => [k, typeof rawCounts[k] === 'number' ? (rawCounts[k] as number) : 0]),
  ) as Record<DataCategory, number>;
  const teamCompanies = Array.isArray(s.team_companies)
    ? s.team_companies.map((t) => {
        const r = asRecord(t);
        return { startupId: String(r.startup_id ?? ''), name: typeof r.name === 'string' && r.name.trim() ? r.name : 'A company' };
      })
    : [];
  return { counts, teamCompanies, analyticsOptOut: s.analytics_opt_out === true };
}

export function parseItems(body: Record<string, unknown>): DataItem[] {
  if (!Array.isArray(body.items)) throw invalid('list: missing items');
  return body.items.map((i) => {
    const r = asRecord(i);
    const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v : null);
    return {
      id: String(r.id ?? ''),
      title: str(r.title) ?? 'Untitled',
      subtitle: str(r.subtitle),
      detail: str(r.detail),
      createdAt: str(r.created_at),
    };
  });
}

/**
 * Interpret one delete reply. Pure; exported for tests.
 *   200 → outcome   207 → storage_incomplete (rows ARE gone; carries the outcome)
 *   anything else → the typed refusal
 */
export function interpretDeleteReply({ status, body }: Reply): DeleteOutcome {
  if (status >= 200 && status < 300) {
    if (typeof body.deleted !== 'number') throw invalid('delete: missing deleted');
    const outcome = { deleted: body.deleted, inProgress: typeof body.in_progress === 'number' ? body.in_progress : 0 };
    if (status === 207 || Array.isArray(body.failed)) {
      throw new ManageDataError(
        'storage_incomplete',
        'Deleted, but a stored file could not be removed yet. It is no longer linked to your account; we have been alerted.',
        { status, outcome },
      );
    }
    return outcome;
  }
  throw mapRefusal(status, body);
}

// -----------------------------------------------------------------------------
// Calls
// -----------------------------------------------------------------------------

export async function fetchDataSummary(): Promise<DataSummary> {
  const reply = await call('summary');
  if (reply.status !== 200) throw mapRefusal(reply.status, reply.body);
  return parseSummary(reply.body);
}

export async function fetchDataItems(category: DataCategory): Promise<DataItem[]> {
  const reply = await call('list', { category });
  if (reply.status !== 200) throw mapRefusal(reply.status, reply.body);
  return parseItems(reply.body);
}

export async function deleteDataItems(category: DataCategory, target: DeleteTarget): Promise<DeleteOutcome> {
  const payload = 'all' in target ? { all: true } : { ids: target.ids };
  return interpretDeleteReply(await call('delete', { category, ...payload }));
}

export async function setAnalyticsOptOut(optOut: boolean): Promise<boolean> {
  const reply = await call('analytics_preference', { opt_out: optOut });
  if (reply.status !== 200) throw mapRefusal(reply.status, reply.body);
  return reply.body.analytics_opt_out === true;
}

// -----------------------------------------------------------------------------
// Cache: what a deletion must refresh elsewhere in the app
// -----------------------------------------------------------------------------

export const manageDataKeys = {
  all: (userId: string | null | undefined) => ['manage-data', userId ?? 'anonymous'] as const,
  summary: (userId: string | null | undefined) => ['manage-data', userId ?? 'anonymous', 'summary'] as const,
  items: (userId: string | null | undefined, category: DataCategory) =>
    ['manage-data', userId ?? 'anonymous', 'items', category] as const,
  company: (userId: string | null | undefined) => ['manage-data', userId ?? 'anonymous', 'company'] as const,
};

/**
 * The EXISTING query keys each category's data is read under elsewhere, so the
 * rest of the app reflects a deletion without a reload. Prefixes: invalidating
 * ['dataRoomDocuments'] refreshes every startup's list. Exported for tests.
 */
export function crossAppKeys(category: DataCategory): QueryKey[] {
  switch (category) {
    case 'data_room_document':
      // queryKeys.dataRoomDocuments(startupId) = ['dataRoomDocuments', startupId]
      // and dataRoomViewKeys.forStartup(startupId) = ['data-room-view-stats', startupId] (view counts)
      return [[queryKeys.dataRoomDocuments('')[0]], [dataRoomViewKeys.forStartup('')[0]]];
    case 'deck_analysis':
      return [deckAnalysesQueryKey];
    case 'dd_note':
      return [['shared-dd']];
    case 'feedback':
      return [['admin-feedback']];
    case 'analytics_events':
      return [['admin-user-activity']];
    default:
      // dd_conversation: the chat holds its history in local state, read fresh per session.
      // avatar: read from the auth profile — see LocalEffects.refreshProfile.
      return [];
  }
}

/** Side effects outside React Query, injected so they can be tested. */
export interface LocalEffects {
  /** Drop deleted documents from the founder data-room store. */
  removeDataRoomDocuments(ids: string[]): void;
  /** Re-read the auth profile (avatar). */
  refreshProfile(): Promise<unknown> | void;
}

/**
 * After a deletion — success OR storage_incomplete, since the rows are gone
 * either way: refresh this screen, every cross-app key, and the local stores.
 */
export async function afterDelete(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  userId: string | null | undefined,
  category: DataCategory,
  deletedIds: string[],
  effects: LocalEffects,
): Promise<void> {
  const keys: QueryKey[] = [manageDataKeys.all(userId), ...crossAppKeys(category)];
  await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  try {
    if (category === 'data_room_document' && deletedIds.length) effects.removeDataRoomDocuments(deletedIds);
    if (category === 'avatar') await effects.refreshProfile();
  } catch (err) {
    reportError(err, { context: 'manageData.afterDelete.localEffects', level: 'warning' });
  }
}

// -----------------------------------------------------------------------------
// Hooks
// -----------------------------------------------------------------------------

/** A refusal (step-up, demo, offline) answers the same every time; only retry the rest. */
export function retryTransient(failureCount: number, err: unknown): boolean {
  if (err instanceof ManageDataError && err.kind !== 'failed') return false;
  return failureCount < 2;
}

export function useDataSummary(userId: string | null | undefined) {
  return useQuery<DataSummary>({
    queryKey: manageDataKeys.summary(userId),
    queryFn: fetchDataSummary,
    enabled: !!userId && manageDataAvailable(),
    staleTime: 0,
    retry: retryTransient,
  });
}

export function useDataItems(userId: string | null | undefined, category: DataCategory | null) {
  return useQuery<DataItem[]>({
    queryKey: manageDataKeys.items(userId, category ?? 'feedback'),
    queryFn: () => fetchDataItems(category as DataCategory),
    enabled: !!userId && !!category && categoryMeta(category).mode === 'items' && manageDataAvailable(),
    staleTime: 0,
    retry: retryTransient,
  });
}

export interface DeleteVariables {
  category: DataCategory;
  target: DeleteTarget;
  /** The ids the person was shown (for "all", the listed ones), for local stores. */
  shownIds: string[];
}

export function useDeleteDataItems(userId: string | null | undefined, effects: LocalEffects) {
  const queryClient = useQueryClient();
  return useMutation<DeleteOutcome, Error, DeleteVariables>({
    mutationFn: ({ category, target }) => deleteDataItems(category, target),
    onSettled: async (_data, error, vars) => {
      // A 207 deleted the rows too; a refusal deleted nothing but a refetch is harmless.
      if (!error || (error instanceof ManageDataError && error.kind === 'storage_incomplete')) {
        await afterDelete(queryClient, userId, vars.category, vars.shownIds, effects);
      }
    },
  });
}

/**
 * The ONE way the usage-analytics choice is changed — Settings' switch and
 * Manage my data's switch both use it. The choice lives on the account
 * (profiles.analytics_opt_out); the tracker's consent gate is derived from it
 * (AuthProvider → setAnalyticsConsent), so there is no second copy to drift.
 *
 * Turning analytics OFF closes the gate at once, before the server answers:
 * nothing more is sent while the request is in flight. On failure the gate is
 * put back to what the account still says, so the device never disagrees with
 * the account. Turning it ON waits for the server. Either way the profile is
 * re-read on success, which is what every other reader sees.
 */
export function useSetAnalyticsOptOut(userId: string | null | undefined) {
  const queryClient = useQueryClient();
  const { profile, refreshProfile } = useAuth();
  const accountOptOut = profile?.analytics_opt_out;
  return useMutation<boolean, Error, boolean>({
    mutationFn: setAnalyticsOptOut,
    onMutate: (optOut) => {
      if (optOut && userId) setAnalyticsConsent(userId, false);
    },
    onSuccess: async (optedOut) => {
      if (userId) setAnalyticsConsent(userId, !optedOut);
      await Promise.all([
        refreshProfile(),
        queryClient.invalidateQueries({ queryKey: manageDataKeys.summary(userId) }),
      ]);
    },
    onError: () => {
      if (userId && typeof accountOptOut === 'boolean') setAnalyticsConsent(userId, !accountOptOut);
    },
  });
}

// -----------------------------------------------------------------------------
// Company data — shown, never offered for deletion
// -----------------------------------------------------------------------------

/**
 * Interview transcripts are COMPANY data, not personal data of the founder who
 * spoke (owner decision 2026-10-18): they describe the company and feed its
 * profile. They are therefore never a delete category here — they are refreshed
 * by running a new interview. The copy deliberately says nothing about what
 * happens to them when an account is erased: that behaviour is being redesigned
 * (phase 119, company lifecycle), and this line must stay true either way.
 */
export interface CompanyDataLine {
  title: string;
  body: string;
  actionLabel: string;
  /** The EXISTING interview entry point (startup-dashboard / score use the same). */
  href: '/(startup)/interview';
}

/** Pure; exported for tests. A missing name degrades to a generic noun. */
export function companyDataLine(companyName: string | null | undefined): CompanyDataLine {
  const name = companyName?.trim() ? companyName.trim() : 'your company';
  return {
    title: 'Interview transcripts',
    body: `Interview transcripts belong to ${name} and are part of its profile. You can refresh them with a new interview.`,
    actionLabel: 'Refresh',
    href: '/(startup)/interview',
  };
}

export interface FounderCompany {
  startupId: string;
  name: string | null;
}

/**
 * The founder's company (active membership, via the canonical resolver) and
 * its name read from the DB — not the local profile store, which can carry a
 * previous session's company. `null` when the person runs no company.
 */
export async function fetchFounderCompany(): Promise<FounderCompany | null> {
  const startupId = await fetchFounderStartupId();
  if (!startupId) return null;
  const { data, error } = await supabase.from('startup_profiles').select('name').eq('id', startupId).maybeSingle();
  if (error) throw error;
  return { startupId, name: data?.name ?? null };
}

export function useFounderCompany(userId: string | null | undefined, enabled: boolean) {
  return useQuery<FounderCompany | null>({
    queryKey: manageDataKeys.company(userId),
    queryFn: async () => {
      try {
        return await fetchFounderCompany();
      } catch (err) {
        // Informational only: the screen hides the line rather than failing.
        reportError(err, { context: 'manageData.founderCompany', level: 'warning' });
        throw err;
      }
    },
    enabled: !!userId && enabled && manageDataAvailable(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

/** "12 Oct 2026" in the device locale; '' when absent. */
export function formatItemDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  return d.toLocaleDateString(APP_LOCALE, { day: 'numeric', month: 'short', year: 'numeric' });
}
